"use client";

import { useMemo } from "react";
import { WagmiProvider } from "wagmi";
import { ConnectionProvider, WalletProvider } from "@solana/wallet-adapter-react";
import { PhantomWalletAdapter, SolflareWalletAdapter } from "@solana/wallet-adapter-wallets";
import { AptosWalletAdapterProvider } from "@aptos-labs/wallet-adapter-react";
import { wagmiConfig, SOLANA_RPC, APTOS_NETWORK } from "@/lib/wallet-config";

/**
 * One provider per ecosystem, nested. They're independent: connecting a Solana wallet does
 * not disturb an EVM connection, which is what lets a user link wallets across chains to a
 * single account.
 */
const WalletProviders = ({ children }: { children: React.ReactNode }) => {
  // Explicit adapters for wallets that predate the Wallet Standard; standard-compliant ones
  // (Backpack, OKX, Trust, …) are auto-discovered and would be duplicated if also listed.
  const solanaWallets = useMemo(
    () => [new PhantomWalletAdapter(), new SolflareWalletAdapter()],
    []
  );

  return (
    <WagmiProvider config={wagmiConfig}>
      <ConnectionProvider endpoint={SOLANA_RPC}>
        {/* autoConnect reconnects a previously-authorised wallet on load; it does NOT sign
            anything, so it can't silently create a session. */}
        <WalletProvider wallets={solanaWallets} autoConnect>
          <AptosWalletAdapterProvider
            autoConnect
            dappConfig={{ network: APTOS_NETWORK }}
            onError={(e) => console.error("[aptos-wallet]", e)}
          >
            {children}
          </AptosWalletAdapterProvider>
        </WalletProvider>
      </ConnectionProvider>
    </WagmiProvider>
  );
};

export default WalletProviders;
